import React, { useEffect, useRef } from "react";

import classnames from "classnames";

import { Input, Select } from "clearsense-ui/components";
import { LeftMinimalArrow, RightMinimalArrow } from "icons";

import Button from "./Button";
import Typography from "./Typography";

const Pagination = ({
  currentPage,
  setCurrentPage,
  totalRecordsCount,
  pageSize = 10,
  pageSizeOptionsList,
  showResultsPerPage = false,
  setPageSize,
  useInputForPageNumber = false,
  className,
}) => {
  const inputRef = useRef();

  currentPage = parseInt(currentPage);
  totalRecordsCount = parseInt(totalRecordsCount) || "";
  pageSize = parseInt(pageSize);

  const totalPages = Math.ceil(totalRecordsCount / pageSize);

  const options = Array.from({ length: totalPages }, (_, index) => {
    return { value: index + 1, label: index + 1 };
  });

  const pageSizeOptions = pageSizeOptionsList
    ? Array.from(pageSizeOptionsList, value => ({ value: value, label: value }))
    : Array.from({ length: 7 }, (_, index) => ({
        value: 10 + index,
        label: 10 + index,
      }));

  const nextPage = () => setCurrentPage(currentPage + 1);

  const prevPage = () => setCurrentPage(currentPage - 1);

  const handleInputChange = value => {
    const parsedValue = parseInt(value);
    if (parsedValue < 1) inputRef.current.value = 1;
    if (parsedValue > totalPages) inputRef.current.value = totalPages;

    let timer;
    timer = setTimeout(() => {
      if (!parsedValue) setCurrentPage(1);
      else if (parsedValue < 1) setCurrentPage(1);
      else if (parsedValue > totalPages) setCurrentPage(totalPages);
      else setCurrentPage(parsedValue);

      clearInterval(timer);
    }, 300);
  };

  const onFocusLoss = event => {
    const value = parseInt(event.target.value);
    if (isNaN(value)) inputRef.current.value = 1;
  };

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = currentPage;
  }, [totalRecordsCount]);

  return (
    <div
      className={classnames("cs-ui-pagination", className, {
        "w-full": !showResultsPerPage,
      })}
      data-testid="cs-ui-pagination"
    >
      {showResultsPerPage && (
        <div className="cs-ui-pagination__selector">
          <span>Results per page</span>
          <Select
            value={{ value: pageSize, label: pageSize }}
            options={pageSizeOptions}
            onChange={option => setPageSize(option.value)}
            maxMenuHeight={200}
            menuPlacement="auto"
            className="flex-shrink-0 w-16"
            strategy="fixed"
          />
        </div>
      )}
      <div
        className={classnames("cs-ui-pagination__right", {
          "w-full flex justify-between": !showResultsPerPage,
        })}
      >
        <Typography className="cs-ui-pagination__text">
          <span>Showing</span>
          <Typography className="cs-ui-pagination__text--highlight">
            {(currentPage - 1) * pageSize + 1 < totalRecordsCount
              ? (currentPage - 1) * pageSize + 1
              : totalRecordsCount}
          </Typography>
          <span>to</span>
          <span className="cs-ui-pagination__text--highlight">
            {currentPage * pageSize < totalRecordsCount
              ? currentPage * pageSize
              : totalRecordsCount}
          </span>
          <span>of</span>
          <Typography className="cs-ui-pagination__text--highlight">
            {totalRecordsCount}
          </Typography>
          <span>results</span>
        </Typography>
        <div className="cs-ui-pagination__selector">
          <Button
            style="text"
            label={
              <Typography className="cs-ui-pagination__selector--highlight">
                PREV
              </Typography>
            }
            icon={LeftMinimalArrow}
            disabled={currentPage === 1 || totalPages === 0}
            iconPosition="left"
            onClick={() => prevPage()}
          />
          {totalPages > 500 || useInputForPageNumber ? (
            <Input
              type="number"
              ref={inputRef}
              onChange={e => handleInputChange(e.target.value)}
              onBlur={onFocusLoss}
              className="w-16 select"
            />
          ) : (
            <Select
              defaultValue={
                totalPages < 1 ? { value: 0, label: 0 } : { value: 1, label: 1 }
              }
              value={
                totalPages < 1
                  ? { value: 0, label: 0 }
                  : { value: currentPage, label: currentPage }
              }
              options={options}
              onChange={option => setCurrentPage(option.value)}
              maxMenuHeight={200}
              menuPlacement="auto"
              className="w-16 select"
              strategy="fixed"
            />
          )}
          <Typography>of</Typography>
          <Typography className="cs-ui-pagination__selector--highlight">
            {totalPages}
          </Typography>
          <Button
            style="text"
            label={
              <Typography className="cs-ui-pagination__selector--highlight">
                NEXT
              </Typography>
            }
            icon={RightMinimalArrow}
            disabled={currentPage === totalPages || totalPages === 0}
            iconPosition="right"
            onClick={() => nextPage()}
          />
        </div>
      </div>
    </div>
  );
};

export default Pagination;
